import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";

/**
 * Guest review dialog — hosts rate a traveller after checkout.
 * - Star rating 1..5 (required)
 * - Positive + negative tag chips (multi-select)
 * - Optional free-text comment (internal)
 * The dialog auto-loads any existing review for the booking so a re-open
 * behaves as an edit rather than creating a duplicate row.
 */

const POSITIVE = [
  { key: "puntuale", it: "Puntuale", en: "On time" },
  { key: "rispettoso", it: "Rispettoso", en: "Respectful" },
  { key: "silenzioso", it: "Silenzioso", en: "Quiet" },
  { key: "comunicativo", it: "Comunicativo", en: "Communicative" },
  { key: "pulito", it: "Pulito", en: "Tidy" },
  { key: "educato", it: "Educato", en: "Polite" },
  { key: "flessibile", it: "Flessibile", en: "Flexible" },
  { key: "sorridente", it: "Sorridente", en: "Smiling" },
];
const NEGATIVE = [
  { key: "ritardo", it: "In ritardo", en: "Late" },
  { key: "rumoroso", it: "Rumoroso", en: "Noisy" },
  { key: "danni", it: "Danni", en: "Damages" },
  { key: "poco_comunicativo", it: "Poco comunicativo", en: "Uncommunicative" },
  { key: "difficile", it: "Difficile", en: "Difficult" },
  { key: "sporco", it: "Sporco", en: "Messy" },
];

export default function GuestReviewDialog({ open, booking, onClose, onSaved }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [tags, setTags] = useState([]);
  const [comment, setComment] = useState("");
  const [existingId, setExistingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !booking?.id) return;
    let alive = true;
    setLoading(true);
    // Pre-fill from any existing review so the host can iterate on their
    // rating instead of getting a "duplicate" toast.
    api.get(`/guest-reviews/for-booking/${booking.id}`)
      .then((r) => {
        if (!alive) return;
        if (r.data) {
          setExistingId(r.data.id);
          setRating(r.data.rating || 5);
          setTags(r.data.tags || []);
          setComment(r.data.comment || "");
        } else {
          setExistingId(null);
          setRating(5); setTags([]); setComment("");
        }
      })
      .catch(() => { setExistingId(null); setRating(5); setTags([]); setComment(""); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, booking?.id]);

  const toggleTag = (k) => {
    setTags((cur) => cur.includes(k) ? cur.filter((t) => t !== k) : [...cur, k]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/guest-reviews", {
        booking_id: booking.id, rating, tags,
        comment: comment.trim() || null,
      });
      toast.success(existingId
        ? (IT ? "Recensione aggiornata" : "Review updated")
        : (IT ? "Recensione salvata" : "Review saved"));
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore salvataggio" : "Save error"));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!existingId) return;
    if (!window.confirm(IT ? "Eliminare la recensione?" : "Delete this review?")) return;
    setSaving(true);
    try {
      await api.delete(`/guest-reviews/${existingId}`);
      toast.success(IT ? "Recensione eliminata" : "Review deleted");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore eliminazione" : "Delete error"));
    } finally { setSaving(false); }
  };

  const list = IT ? POSITIVE.map((t) => ({ ...t, label: t.it })) : POSITIVE.map((t) => ({ ...t, label: t.en }));
  const nlist = IT ? NEGATIVE.map((t) => ({ ...t, label: t.it })) : NEGATIVE.map((t) => ({ ...t, label: t.en }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="guest-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary"/>
            {IT ? "Recensisci l'ospite" : "Review the guest"}
          </DialogTitle>
          <DialogDescription>
            {booking?.guest_name && <span className="font-semibold">{booking.guest_name}</span>}
            {booking?.check_in && booking?.check_out && (
              <span className="text-muted-foreground"> · {booking.check_in} → {booking.check_out}</span>
            )}
            <br/>
            <span className="text-xs italic text-muted-foreground">
              {IT
                ? "Solo tu vedi il commento. Rating + tag sono visibili agli altri host che ospiteranno questo viaggiatore."
                : "Only you see the comment. Rating + tags are visible to other hosts who host this traveller next."}
            </span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-center text-sm text-muted-foreground italic">{IT ? "Caricamento…" : "Loading…"}</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-1 py-2" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hover || rating) >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    data-testid={`review-star-${n}`}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={IT ? `${n} stelle` : `${n} stars`}
                  >
                    <Star className={`w-8 h-8 ${filled ? "fill-primary text-primary" : "text-muted-foreground/40"}`}/>
                  </button>
                );
              })}
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                {IT ? "Punti di forza" : "Strengths"}
              </div>
              <div className="flex flex-wrap gap-2">
                {list.map((t) => {
                  const on = tags.includes(t.key);
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => toggleTag(t.key)}
                      data-testid={`review-tag-${t.key}`}
                      className={`text-xs px-3 py-1 rounded-full border transition-all ${on
                        ? "bg-sage/15 border-sage text-sage font-medium"
                        : "border-border text-muted-foreground hover:border-sage/50 hover:text-sage"}`}
                    >
                      {on ? "✓ " : ""}{t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                {IT ? "Aree di miglioramento" : "Areas to improve"}
              </div>
              <div className="flex flex-wrap gap-2">
                {nlist.map((t) => {
                  const on = tags.includes(t.key);
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => toggleTag(t.key)}
                      data-testid={`review-tag-${t.key}`}
                      className={`text-xs px-3 py-1 rounded-full border transition-all ${on
                        ? "bg-destructive/10 border-destructive text-destructive font-medium"
                        : "border-border text-muted-foreground hover:border-destructive/50 hover:text-destructive"}`}
                    >
                      {on ? "✓ " : ""}{t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                {IT ? "Nota interna (opzionale)" : "Internal note (optional)"}
              </div>
              <Textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={IT ? "Dettagli utili per il tuo storico…" : "Details useful for your own record…"}
                data-testid="review-comment"
                maxLength={500}
              />
            </div>

            <div className="flex flex-wrap justify-between gap-2 pt-2">
              {existingId ? (
                <Button
                  variant="outline"
                  onClick={remove}
                  disabled={saving}
                  className="border-destructive/40 text-destructive hover:bg-destructive hover:text-white"
                  data-testid="review-delete-btn"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1"/> {IT ? "Elimina" : "Delete"}
                </Button>
              ) : <span/>}
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} data-testid="review-cancel-btn">
                  {IT ? "Annulla" : "Cancel"}
                </Button>
                <Button
                  onClick={save}
                  disabled={saving || rating < 1}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 lift"
                  data-testid="review-save-btn"
                >
                  {saving ? "…" : existingId ? (IT ? "Aggiorna" : "Update") : (IT ? "Salva recensione" : "Save review")}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
